import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";
import {
  cLoadPreviousEvents,
  cLoadComposeParentPage,
} from "lib/contentful-client";
import { DateTime } from "luxon";
import { upperCase } from "lodash";
import { useRouter } from "next/router";
import generateRandomImagePlaceholderPath from "lib/generate-random-image-placeholder";
import Button from "components/Button";
import LoadingSpinner from "components/Lottie/loading";
import EventListItem from "components/EventListItem";

const Container = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 1.5625rem;
  width: 100%;
`;

const EventsContainer = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 2.625rem;
  margin-bottom: 2rem;
  max-width: 76.25rem;
  padding-bottom: 2.625rem;
  width: 100%;

  ${({ showBttmBorder }) =>
    showBttmBorder &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.themeColors.darkGreen3};
    `}
`;

const SectionTitle = styled.div`
  font-size: 2rem;
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  margin: 3.875rem 0 2rem 0;
  font-weight: 600;

  ${({ theme }) => theme.mq.lg`
    font-size: ${theme.fontSize(6)};
  `};
`;

const ShowMoreButton = styled(Button)`
  margin-bottom: 1.5625rem;
  max-width: 20rem;

  ${({ theme }) => theme.mq.md`
    margin-bottom: 3.125rem;
  `}
`;

const Spacer = styled.div`
  height: 1.5625rem;
`;

// eslint-disable-next-line no-unused-vars
function PreviousEventsBlock({ lat, lng }) {
  const { asPath } = useRouter();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(1);
  const pageSize = 3;

  const skipMultiplier = currentPage === 1 ? 0 : currentPage - 1;
  const skip = skipMultiplier > 0 ? pageSize * skipMultiplier : 0;

  const amtOfRequestedEvents = events.length;
  const isShowMoreBttnVisible = amtOfRequestedEvents < totalEvents;

  const getEvents = async () => {
    const eventsData = await cLoadPreviousEvents({
      slug: "pageEventDetail",
      skip,
    });

    const composeData = await cLoadComposeParentPage({
      items: eventsData.items,
    });

    setEvents((oldArray) => [...oldArray, ...composeData]);
    setTotalEvents(eventsData.total);
    setIsLoading(false);
  };

  useEffect(() => {
    getEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <Container>
      <SectionTitle>Past events</SectionTitle>
      <EventsContainer showBttmBorder={isShowMoreBttnVisible}>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          events.map((event) => {
            const composePage = event?.compose?.items?.[0]?.fields;
            const eventSlug = composePage?.slug;

            const eventTitle = event?.fields?.eventTitle;
            const eventUtcDate = event?.fields?.date;
            const eventEndDate = event?.fields?.endDate;

            const eventStreetAddress = event?.fields?.streetAddress;
            const eventWebsite = event?.fields?.websiteUrl;
            const eventId = event?.sys?.id;
            const eventLat = event?.fields?.address?.lat;
            const eventLng = event?.fields?.address?.lon;
            const eventImage = event?.fields?.image?.fields?.file?.url;

            const eventDate = DateTime.fromISO(eventUtcDate);
            const eventDay = eventDate.get("day").toString();
            const eventTime = eventDate.toLocaleString(DateTime.TIME_SIMPLE);
            const eventDayAbbrev = upperCase(eventDate.toFormat("ccc"));
            const eventMonthAbbrev = upperCase(eventDate.toFormat("LLL"));
            const eventTimeZone = eventDate.toFormat("ZZZZ");
            const eventDetailLink = `${asPath}/${eventSlug}`;
            return (
              <EventListItem
                title={eventTitle}
                eventDate={eventDate}
                address={eventStreetAddress}
                eventId={eventId}
                website={eventWebsite}
                dateNumber={eventDay}
                dateDay={eventDayAbbrev}
                month={eventMonthAbbrev}
                eventEndDate={eventEndDate}
                image={
                  `https:${eventImage}` ||
                  generateRandomImagePlaceholderPath(eventId)
                }
                key={eventId}
                lat={eventLat}
                lng={eventLng}
                eventDetailLink={eventDetailLink}
                time={eventTime}
                timezone={eventTimeZone}
              />
            );
          })
        )}
      </EventsContainer>
      {isShowMoreBttnVisible ? (
        <ShowMoreButton
          type="button"
          outlineType="none"
          onClick={(e) => {
            e.preventDefault();
            setCurrentPage(currentPage + 1);
          }}
        >
          Show more
        </ShowMoreButton>
      ) : (
        <Spacer />
      )}
    </Container>
  );
}

export default PreviousEventsBlock;

PreviousEventsBlock.propTypes = {
  lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
